import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from "react";
import {
  useLocation,
  useParams,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { url } from "../Constant/TextConstant";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ProductDetails.css";
import ReviewSection from "./ReviewSection";
import { useCart } from "../utils/cartContext";
import { useNetworkStatus } from "../utils/userNetworkStatus.js";
import { useUserLocation } from "../utils/useUserLocation.js";
import Header from "../files/Header.jsx";
import RelatedProducts from "../files/RelatedProductScreen/RelatedProduct.jsx";
import { encryptData } from "../utils/encryption.js";
import { Helmet } from "react-helmet";
import { useAuth } from "../utils/AuthContext.js";
import { apiFetch } from "../utils/apiClient.js";

const ProductDetails = () => {
  // Router related state
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Product data state
  const [product, setProduct] = useState(state?.product || null);
  const [loading, setLoading] = useState(!state?.product);
  const [error, setError] = useState(null);

  // Product selection state
  const [itemQuantity, setItemQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [variantData, setVariantData] = useState([]);

  // Product display state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [showZoom, setShowZoom] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [ratingCounts, setRatingCounts] = useState({});

  // Location hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Save selected category to localStorage
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handle modal close
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };

  const [userIpAddress, setUserIpAddress] = useState(null);

  // UI state
  const [isScrolled, setIsScrolled] = useState(false);

  const containerRef = useRef(null);
  const infoRef = useRef(null);

  // Context
  const { addToCart: contextAddToCart } = useCart();

  // Location effect
  let isMounted = true; // Flag to prevent state updates after unmount

  const fetchIpAddress = async () => {
    try {
      const response = await apiFetch("http://www.geoplugin.net/json.gp");

      if (!isMounted) return; // Exit if component unmounted

      if (!response.ok) {
        throw new Error(`Failed to fetch IP details: ${response.status}`);
      }

      const data = await response.json();
      const ipAddress = data.geoplugin_request;

      if (!isMounted) return;

      setUserIpAddress(ipAddress);

      // Only log if we have both IP and product data
      if (product?.SubCategory2) {
        await logRelatedSubCategory(product?.SubCategory2);
      }
    } catch (error) {
      if (!isMounted) return;
    }
  };

  fetchIpAddress();

  // IP address effect with proper syntax
  useEffect(() => {
    let isMounted = true;

    const fetchIpAddress = async () => {
      try {
        const response = await apiFetch("https://www.geoplugin.net/json.gp");
        if (!response.ok) {
          throw new Error("Failed to fetch IP details");
        }

        const data = await response.json();
        const ipAddress = data.geoplugin_request;

        if (!isMounted) return;

        setUserIpAddress(ipAddress);

        // Only log if we have both IP and product data
        if (product?.subCategory && ipAddress) {
          try {
            const logResponse = await apiFetch(
              `${url.baseAPIUrl}/api/UserIpAddress/RelatedSubcategory`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  Subcategory: product.subCategory,
                  IPAddress: ipAddress,
                }),
              }
            );

            if (!logResponse.ok) {
              throw new Error(`HTTP error! status: ${logResponse.status}`);
            }

            const result = await logResponse.json();
          } catch (logError) {
          }
        }
      } catch (error) {
        if (!isMounted) return;
      }
    };

    fetchIpAddress();

    return () => {
      isMounted = false;
    };
  }, [product?.subCategory, url.baseAPIUrl]); // Proper dependencies

  // Standalone log function (if needed elsewhere in component)
  const logRelatedSubCategory = useCallback(
    async (subCategory) => {
      if (!userIpAddress) {
        return false;
      }

      try {
        const response = await apiFetch(
          `${url.baseAPIUrl}/api/UserIpAddress/RelatedSubcategory`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              Subcategory: subCategory || "",
              IPAddress: userIpAddress,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return true;
      } catch (error) {
        return false;
      }
    },
    [userIpAddress, url.baseAPIUrl]
  );

  // Handle scroll for sticky images
  useEffect(() => {
    const handleScroll = () => {
      if (infoRef.current) {
        const { top } = infoRef.current.getBoundingClientRect();
        setIsScrolled(top < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle product images zoom
  const handleMouseMove = (e) => {
    if (containerRef.current) {
      const { left, top, width, height } =
        containerRef.current.getBoundingClientRect();
      const x = ((e.pageX - left - window.scrollX) / width) * 100;
      const y = ((e.pageY - top - window.scrollY) / height) * 100;
      setZoomPosition({ x, y });
    }
  };

  // Memoize product images
  const productImages = useMemo(() => {
    if (!product) return [];
    const baseUrl = `${url.productImageStartingPath.replace(
      "/thumbnails/",
      "/original/"
    )}`;
    return [
      `${baseUrl}${product.Image1}`,
      product.Image2 ? `${baseUrl}${product.Image2}` : null,
      product.Image3 ? `${baseUrl}${product.Image3}` : null,
      product.Image4 ? `${baseUrl}${product.Image4}` : null,
    ].filter(Boolean);
  }, [product]);

  // Update available sizes
  const updateAvailableSizes = useCallback((variantsData, color) => {
    if (!Array.isArray(variantsData)) return;

    const colorVariants = variantsData.filter(
      (item) => item.ColorName === color
    );

    const allSizes = colorVariants.flatMap((item) => item.Sizes || []);
    const uniqueSizes = [...new Set(allSizes)];

    setAvailableSizes(uniqueSizes);

    if (uniqueSizes.length > 0) {
      const firstSize = uniqueSizes[0];
      setSelectedSize(firstSize);
      fetchUpdatedProductDetails(color, firstSize);
    }
  }, []);

  const fetchUpdatedProductDetails = async (colorName, size) => {
    if (!size || !product?.ProductCode) return;
    const requestUrl = `${url.baseAPIUrl}/api/ProductDetails/GetProductByColorAndSize?productCode=${product.ProductCode}&size=${size}&colorName=${colorName}`;


    try {
      const response = await apiFetch(requestUrl);


      if (!response.ok) throw new Error("Failed to fetch updated product");

      const data = await response.json();

      if (Array.isArray(data) && data.length > 0) {
        setProduct(data[0]);
        fetchProductVariants(data[0].product.ProductCode);
        fetchReviews(data[0].product.StoreMobile, data[0].product.ProductCode);
      } else {
      }
    } catch (err) {
    }
  };

  // Fetch product variants
  const fetchProductVariants = useCallback(
    async (productCode) => {
      try {
        const response = await apiFetch(
          `${url.baseAPIUrl}/api/ProductDetails/GetSizeAndColorByProductCode?ProductCode=${productCode}`
        );

        if (!response.ok) throw new Error("Failed to fetch variants");

        const data = await response.json();
        setVariantData(data);

        const isGenericVariant =
          data.length === 1 && !data[0].ColorName && !data[0].Image;

        if (isGenericVariant) {
          const genericSizes = data[0].Sizes || [];
          setColorOptions([]);
          setSelectedColor(null);
          setAvailableSizes(genericSizes);
          setSelectedSize(genericSizes[0] || null);
          return;
        }

        const colors = data
          .filter((item) => item.ColorName && item.Image)
          .map((item) => ({
            color: item.ColorName,
            image: item.Image,
            sizes: item.Sizes || [],
          }));

        setColorOptions(colors.map(({ color, image }) => ({ color, image })));

        if (colors.length > 0) {
          const firstColor = colors[0];
          setSelectedColor(firstColor.color);
          updateAvailableSizes(data, firstColor.color);
        }
      } catch (err) {
      }
    },
    [updateAvailableSizes]
  );

  // Fetch reviews
  const fetchReviews = useCallback(async (storeMobile, productCode) => {
    try {
      const response = await apiFetch(
        `${url.baseAPIUrl}/api/ShopkeeperLikeReviewAndCall/GetReviewByMobile?StoreMobile=${storeMobile}`
      );

      if (!response.ok) throw new Error("Failed to fetch reviews");

      const data = await response.json();
      const productReviews = data.filter(
        (review) => review.ProductCode === productCode
      );
      setReviews(productReviews);

      const counts = {};
      for (let i = 1; i <= 5; i++) {
        counts[i] = productReviews.filter((r) => r.Rating === i).length;
      }
      setRatingCounts(counts);
    } catch (err) {
    }
  }, []);

  // Fetch product details
  useEffect(() => {
    if (state?.product) {
      setProduct(state.product);

      // Preselect the product's actual color and size if available
      if (state.product.ColorName) {
        setSelectedColor(state.product.ColorName);
      }
      if (state.product.Size) {
        setSelectedSize(state.product.Size);
      }

      fetchProductVariants(state.product.ProductCode);

      fetchReviews(state.product.StoreMobile, state.product.ProductCode);
      setLoading(false);
      return;
    }

    const fetchProductDetails = async () => {
      try {
        setLoading(true);

        const response = await apiFetch(
          `${url.baseAPIUrl}/api/ProductDetails/BindProductDetailsByID?ID=${id}`
        );

        if (!response.ok) throw new Error("Failed to fetch product");

        const data = await response.json();

        if (data.length > 0) {
          const product = data[0];
          setProduct(product);

          if (product.ColorName) {
            setSelectedColor(product.ColorName);
          }
          if (product.Size) {
            setSelectedSize(product.Size);
          }

          fetchProductVariants(product.ProductCode);
          fetchReviews(product.StoreMobile, product.ProductCode);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [id, state, fetchProductVariants]);

  // Handle color selection
  const handleColorSelect = useCallback(
    (color) => {
      setSelectedColor(color);
      updateAvailableSizes(variantData, color);
    },
    [variantData, updateAvailableSizes]
  );

  // Handle size selection
  const handleSizeSelect = useCallback(
    (size) => {
      setSelectedSize(size);
      fetchUpdatedProductDetails(selectedColor ?? "", size);
    },
    [selectedColor]
  );

  // Handle quantity changes
  // Handle quantity change (safe to call anywhere)
  const handleQuantityChange = useCallback((newQuantity) => {
    if (newQuantity < 1) return;
    setItemQuantity(newQuantity); // this will update asynchronously
  }, []);

  // Track real-time update of quantity
  useEffect(() => {
  }, [itemQuantity]);

  // Add to cart function

  const handleAddToCart = () => {
    if (product.StoreMobile === currentUser) {
      const userConfirmed = window.confirm(
        "This is your own product. You can't add it to the cart."
      );
      // You could do something with userConfirmed if needed.
      return;
    }

    if (!selectedSize) {
      toast.error("Please select a size", {
        position: "bottom-right",
        autoClose: 3000,
        style: { backgroundColor: "#ff4444", color: "white" },
      });
      return;
    }

    // List of required fields with their display names
    const requiredFields = [
      { key: "ProductCode", name: "Product Code" },
      { key: "ProductName", name: "Product Name" },
      { key: "OurPrice", name: "Price" },
      { key: "StoreMobile", name: "Store Mobile" },
      { key: "StoreName", name: "Store Name" },
    ];

    // Check for missing required fields
    const missingFields = requiredFields.filter(
      (field) => !product?.[field.key]
    );

    if (missingFields.length > 0) {
      toast.error(
        <div>
          Missing product information:
          <ul style={{ marginTop: "5px", paddingLeft: "20px" }}>
            {missingFields.map((field) => (
              <li key={field.key}>{field.name}</li>
            ))}
          </ul>
          Please try again later.
        </div>,
        {
          position: "bottom-right",
          autoClose: 5000,
          style: { backgroundColor: "#ff4444", color: "white" },
        }
      );
      return;
    }

    // Prepare the complete product data
    const productToAdd = {
      ProductId: product?.ID || product?.ProductCode || 0,
      ID: product?.ID || product?.ProductCode || 0,

      ProductName: product?.ProductName || "",
      ProductCode: product?.ProductCode || "",
      OurPrice: product?.OurPrice || 0,
      Price: product.Mrp,
      CustomerMobile: currentUser,
      Mrp: product?.Mrp || 0,
      DiscountPercentage: product?.Discount || 0,
      Image1: product?.Image1 || "",
      Image2: product?.Image2 || "",
      StoreMobile: product?.StoreMobile || "",
      StoreName: product?.StoreName || "",
      Category: product?.Category || "",
      SubCategory1: product?.SubCategory1 || "",
      SubCategory2: product?.SubCategory2 || "",
      Cashback: product?.Cashback || 0,
      Size: selectedSize,
      ColorName: selectedColor || "",
      quantity: itemQuantity,
      Total: (product?.OurPrice || 0) * itemQuantity,
      Latitude: product?.Latitude || 0,
      Longitude: product?.Longitude || 0,
      AverageRating: product?.AverageRating || 0,
      ReviewCount: reviews?.length || 0,
      stock: product?.Stock || 10,
      addedAt: new Date().toISOString(),
    };

    // Add to cart - the CartContext will handle duplicates and show appropriate toasts
    contextAddToCart(productToAdd);
  };

  // Buy now function

  const handleBuyNow = () => {
    if (product.StoreMobile === currentUser) {
      const userConfirmed = window.confirm(
        "This is your own product. You can't buy it."
      );
      // You could do something with userConfirmed if needed.
      return;
    }
    if (!selectedSize) {
      toast.error("Please select a size", {
        position: "bottom-right",
        autoClose: 3000,
        style: { backgroundColor: "#ff4444", color: "white" },
      });
      return;
    }

    // Prepare the product data for checkout
    const productToCheckout = {
      ID: product?.ID || product?.ProductCode || 0,
      ProductName: product?.ProductName || "",
      ProductCode: product?.ProductCode || "",
      OurPrice: product?.OurPrice || 0,
      Mrp: product?.Mrp || 0,
      DiscountPercentage: product?.Discount || 0,
      Image1: product?.Image1 || "",
      Image2: product?.Image2 || "",
      StoreMobile: product?.StoreMobile || product?.storeMobile || "",
      StoreName: product?.StoreName || "",
      Category: product?.Category || "",
      SubCategory1: product?.SubCategory1 || "",
      SubCategory2: product?.SubCategory2 || "",
      Cashback: product?.Cashback || 0,
      Size: selectedSize,
      ColorName: selectedColor || "",
      quantity: itemQuantity,
      Total: (product?.OurPrice || 0) * itemQuantity,
      Latitude: product?.Latitude || 0,
      Longitude: product?.Longitude || 0,
      AverageRating: product?.AverageRating || 0,
      ReviewCount: reviews?.length || 0,
      stock: product?.Stock || 10,
      addedAt: new Date().toISOString(),
    };

    // Navigate to checkout with the product data
    navigate("/checkout", {
      state: {
        cart: [productToCheckout], // Pass as array to match cart structure
        cartTotal: productToCheckout.Total,
        cartCount: 1,
        fromCart: false,
      },
    });
  };

  // Share product function
  // import { toast } from "react-toastify";

  // Enhanced slug creation function
  const createProductSlug = (productName) => {
    return (
      productName
        .toLowerCase()
        .trim()
        // First replace % with '-percent-' to make it readable
        .replace(/%/g, "-percent-")
        // Then replace + with '-plus-'
        .replace(/\+/g, "-plus-")
        // Remove other special characters except hyphens
        .replace(/[^\w\s-]/g, "")
        // Replace spaces with hyphens
        .replace(/\s+/g, "-")
        // Collapse multiple hyphens
        .replace(/-+/g, "-")
        // Limit length
        .slice(0, 60)
        // Remove trailing hyphen
        .replace(/-$/, "")
    );
  };

  // http://localhost:3000/product/3165/best-android-speaker
  const shareProduct = useCallback(() => {
    const productUrl = `https://nearmeind.com/product/${id}/${createProductSlug(
      product?.ProductName
    )}`;
    const productName = product?.ProductName || "Check this out on NearMe!";
    const shareText = `🛍️ ${productName}\n\nCheck it out on NearMe 👇\n${productUrl}`;

    if (navigator.share) {
      navigator
        .share({
          title: productName,
          text: shareText,
          // Do not pass `url` separately to avoid hiding text
        })
        .catch((err) => {
          showCopyOption(); // fallback if user cancels or it fails
        });
    } else {
      showCopyOption();
    }

    function showCopyOption() {
      if (
        window.confirm(
          "Sharing not supported. Would you like to copy the product info to clipboard?"
        )
      ) {
        navigator.clipboard
          .writeText(shareText)
          .then(() => {
            toast.success("Product info copied to clipboard!", {
              position: "bottom-right",
              autoClose: 3000,
              style: { backgroundColor: "#4CAF50", color: "white" },
            });
          })
          .catch((err) => {

            toast.error("Failed to copy. Please try again.", {
              position: "bottom-right",
              autoClose: 3000,
              style: { backgroundColor: "#ff4444", color: "white" },
            });
          });
      }
    }
  }, [id, product]);

  if (loading) {
    return (
      <>
        {" "}
        <Helmet>
          <title>Loading Product... | NearMe</title>
        </Helmet>
        <div className="loading-container">
          <div className="shimmer-header"></div>
          <div className="shimmer-gallery"></div>
          <div className="shimmer-details">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="shimmer-line"></div>
            ))}
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {" "}
        <Helmet>
          <title>Product Error | NearMe</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="error-message">Error: {error}</div>;
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Helmet>
          <title>Product Not Found | NearMe</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="not-found">Product not found</div>
      </>
    );
  }

  return (
    <>
      {" "}
      <Helmet>
        <title>
          {product?.ProductName
            ? `${product.ProductName} | ${product.StoreName || "NearMe"}`
            : "Product Details | NearMe"}
        </title>
        <meta
          name="description"
          content={
            product?.ProductDescription ||
            `Buy ${product?.ProductName || "this product"} from ${
              product?.StoreName || "local stores"
            } at best prices.`
          }
        />
        <meta
          name="keywords"
          content={`${product?.ProductName || "product"}, ${
            product?.Category || ""
          }, ${product?.SubCategory1 || ""}, buy online, ${
            product?.StoreName || ""
          }, NearMe`}
        />
        <meta
          property="og:title"
          content={product?.ProductName || "Product Details"}
        />
        <meta
          property="og:description"
          content={
            product?.ProductDescription || `Check out this product on NearMe`
          }
        />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={window.location.href} />
        {product?.Image1 && (
          <meta
            property="og:image"
            content={`${url.productImageStartingPath.replace(
              "/thumbnails/",
              "/original/"
            )}${product.Image1}`}
          />
        )}
      </Helmet>
      <Header
        userLocation={currentLocation}
        setShowModal={setShowModal}
        setUserLocation={updateLocation}
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <div className="product-details-container">
        {/* Image Gallery */}
        <div className={`product-gallery ${isScrolled ? "sticky" : ""}`}>
          <div className="thumbnail-column">
            {productImages.map((img, index) => (
              <div
                key={index}
                className={`thumbnail-wrapper ${
                  index === currentImageIndex ? "active" : ""
                }`}
                onClick={() => setCurrentImageIndex(index)}
              >
                <img
                  src={img.replace("/original/", "/thumbnails/")}
                  alt={`${product.ProductName} thumbnail ${index + 1}`}
                  className="thumbnail"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = img;
                  }}
                />
              </div>
            ))}
          </div>

          <div
            className="main-image-container"
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setShowZoom(true)}
            onMouseLeave={() => setShowZoom(false)}
            ref={containerRef}
          >
            <img
              src={productImages[currentImageIndex]}
              alt={product.ProductName}
              className="main-image"
              loading="eager"
            />

            {showZoom && (
              <div className="zoom-preview">
                <img
                  src={productImages[currentImageIndex]}
                  alt="Zoomed preview"
                  style={{
                    transform: `translate(-${zoomPosition.x}%, -${zoomPosition.y}%)`,
                  }}
                />
              </div>
            )}

            <button className="share-button" onClick={shareProduct}>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="product-info" ref={infoRef}>
          <h1 className="product-title">{product.ProductName}</h1>
          <p className="product-brand">Brand: {product.BrandName}</p>
          <p
            className="product-store-link"
            onClick={() =>
              navigate(`/store-detail/${encryptData(product.StoreMobile || product.storeMobile)}`, {
                state: { storeName: product.StoreName },
              })
            }
          >
            visit the store
          </p>
          {/* Price Section */}
          <div className="price-section">
            {product.Discount > 0 && (
              <div className="discount-badge">-{product.Discount}%</div>
            )}
            <div className="price-row">
              <span className="current-price">₹{product.OurPrice}</span>
              {product.Discount > 0 && (
                <span className="original-price">₹{product.Mrp}</span>
              )}
            </div>
            {product.Discount > 0 && (
              <div className="savings">
                You save: ₹{product.Mrp - product.OurPrice} ({product.Discount}
                %)
              </div>
            )}
          </div>

          {/* Rating */}
          <div className="rating-section">
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <span
                  key={i}
                  className={i < product.AverageRating ? "filled" : ""}
                >
                  ★
                </span>
              ))}
            </div>
            <span>
              {product.AverageRating} ({reviews.length} reviews)
            </span>
          </div>

          {/* Color Selection */}
          {colorOptions.length > 0 && (
            <div className="selection-section">
              <h3>Color:</h3>
              <div className="color-options">
                {colorOptions.map((option, index) => (
                  <div
                    key={index}
                    className={`color-option ${
                      selectedColor === option.color ? "selected" : ""
                    }`}
                    onClick={() => handleColorSelect(option.color)}
                  >
                    <img
                      src={`${url.productImageStartingPath}${option.image}`}
                      alt={option.color}
                    />
                    {/*   <span>{option.color}</span> */}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Size Selection */}
          {availableSizes.length > 0 && (
            <div className="selection-section">
              <h3>Size:</h3>
              <div className="size-options">
                {availableSizes.map((size, index) => (
                  <button
                    key={index}
                    className={`size-option ${
                      selectedSize === size ? "selected" : ""
                    }`}
                    onClick={() => handleSizeSelect(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity Selector */}
          <div className="quantity-section">
            <h3>Quantity:</h3>
            <div className="quantity-selector">
              <button
                onClick={() => handleQuantityChange(itemQuantity - 1)}
                disabled={itemQuantity <= 1}
              >
                -
              </button>
              <span>{itemQuantity}</span>
              <button onClick={() => handleQuantityChange(itemQuantity + 1)}>
                +
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="add-to-cart" onClick={handleAddToCart}>
              Add to Cart
            </button>
            <button className="buy-now" onClick={handleBuyNow}>
              Buy Now
            </button>
          </div>

          {/* Product Details */}
          <div className="details-section">
            <h3>Product Details</h3>
            <table className="details-table">
              <tbody>
                <tr>
                  <td>Category</td>
                  <td>{product.Category}</td>
                </tr>
                <tr>
                  <td>Subcategory</td>
                  <td>{product.SubCategory1}</td>
                </tr>
                <tr>
                  <td>Sold by</td>
                  <td>{product.StoreName}</td>
                </tr>
                <tr>
                  <td>Delivery</td>
                  <td>
                    {product.DeliveryOption === "ON"
                      ? "Available"
                      : "Not available"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Description */}
          {product.ProductDescription && (
            <div className="description-section">
              <h3>Description</h3>
              <p>{product.ProductDescription}</p>
            </div>
          )}
        </div>
      </div>
      <ReviewSection
        storeMobile={product.StoreMobile}
        productCode={product.ProductCode}
      />
      <RelatedProducts productId={product.ID} />
    </>
  );
};

export default memo(ProductDetails);
